/**
 * Los identificadores del cuaderno, comprobados en CÓDIGO: NIF/NIE/CIF con su
 * letra o dígito de control, nº ROMA, nº ROPO, código postal y referencia SIGPAC.
 *
 * Por qué aquí y no «a ojo» del asistente ni del formulario: un NIF con la
 * letra cambiada o un SIGPAC con un dígito de menos tienen el MISMO aspecto que
 * uno bueno, y en un documento que firma el titular y mira un inspector eso es
 * peor que el hueco. La regla es la misma para el formulario de Ajustes, el motor
 * del cuaderno y (con CQ4) el asistente: la voz repite el dato y solo queda firme
 * si pasa por aquí.
 *
 * PURO: sin imports de la aplicación, para que la copia Deno de CQ4 sea literal.
 *
 * E11: estas funciones no escriben nada en ningún sitio. Un NIF nunca va a un
 * log ni a un correo, tampoco cuando no valida.
 */

export type NifKind = "dni" | "nie" | "cif" | "nif_especial";

export type NifCheck =
  | { ok: true; value: string; kind: NifKind }
  | { ok: false; value: string; reason: "empty" | "format" | "control" };

const DNI_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";
const CIF_CONTROL_LETTERS = "JABCDEFGHI";

/** «b-04.123.451» → «B04123451»: mayúsculas y sin espacios, puntos ni guiones. */
export function normalizeNif(raw: string | null | undefined): string {
  return String(raw ?? "").toUpperCase().replace(/[\s.-]/g, "");
}

function dniLetter(num: number): string {
  return DNI_LETTERS[num % 23];
}

/**
 * NIF de persona (DNI, NIE, K/L/M) o de entidad (CIF), con su control.
 *
 *   - DNI: 8 cifras + letra (resto de dividir entre 23).
 *   - NIE: X/Y/Z + 7 cifras + letra (la X vale 0, la Y 1 y la Z 2).
 *   - K/L/M (NIF especiales de persona): letra + 7 cifras + letra, como el DNI.
 *   - CIF: letra de entidad + 7 cifras + control, que es una CIFRA (A, B, E, H),
 *     una LETRA (N, P, Q, R, S, W) o cualquiera de las dos (el resto).
 */
export function checkNif(raw: string | null | undefined): NifCheck {
  const value = normalizeNif(raw);
  if (!value) return { ok: false, value, reason: "empty" };

  let m = /^(\d{8})([A-Z])$/.exec(value);
  if (m) return dniLetter(Number(m[1])) === m[2] ? { ok: true, value, kind: "dni" } : { ok: false, value, reason: "control" };

  m = /^([XYZ])(\d{7})([A-Z])$/.exec(value);
  if (m) {
    const num = Number(`${"XYZ".indexOf(m[1])}${m[2]}`);
    return dniLetter(num) === m[3] ? { ok: true, value, kind: "nie" } : { ok: false, value, reason: "control" };
  }

  m = /^([KLM])(\d{7})([A-Z])$/.exec(value);
  if (m) return dniLetter(Number(m[2])) === m[3] ? { ok: true, value, kind: "nif_especial" } : { ok: false, value, reason: "control" };

  m = /^([ABCDEFGHJNPQRSUVW])(\d{7})([0-9A-J])$/.exec(value);
  if (m) {
    const digits = m[2].split("").map(Number);
    let sum = 0;
    digits.forEach((d, i) => {
      if (i % 2 === 1) sum += d;
      else {
        const x = d * 2;
        sum += Math.floor(x / 10) + (x % 10);
      }
    });
    const digit = (10 - (sum % 10)) % 10;
    const letter = CIF_CONTROL_LETTERS[digit];
    const control = m[3];
    const onlyDigit = "ABEH".includes(m[1]);
    const onlyLetter = "NPQRSW".includes(m[1]);
    const ok = onlyDigit ? control === String(digit) : onlyLetter ? control === letter : control === String(digit) || control === letter;
    return ok ? { ok: true, value, kind: "cif" } : { ok: false, value, reason: "control" };
  }

  return { ok: false, value, reason: "format" };
}

export const isValidNif = (raw: string | null | undefined): boolean => checkNif(raw).ok;

/**
 * POR QUÉ no vale un NIF, en concreto (27-09-2026, Pablo: «el formato de
 * 26.032.427.4 no valida» es verdad pero no ayuda). Para decirle a la persona
 * qué le pasa —le falta la letra, sobra una cifra, la letra de control no
 * cuadra— sin corregirlo nunca por ella. null = vale.
 *
 *   sin_letra        8 cifras y ninguna letra (un DNI sin su letra)
 *   cifra_de_mas     9 cifras y ninguna letra (sobra una cifra, o la letra se dijo como número)
 *   cifras_de_menos  le faltan cifras para su forma (DNI, NIE o CIF)
 *   cifras_de_mas    le sobran cifras para su forma
 *   letra_control    la forma es buena pero la letra (o cifra) de control no cuadra
 *   caracteres       lleva algo que no es letra ni cifra
 *   forma            no tiene la forma de ningún NIF/NIE/CIF
 */
export type DetalleNif = "vacio" | "sin_letra" | "cifra_de_mas" | "cifras_de_menos" | "cifras_de_mas" | "letra_control" | "caracteres" | "forma";

export function detalleNif(raw: string | null | undefined): DetalleNif | null {
  const r = checkNif(raw);
  if (r.ok) return null;
  const v = r.value;
  if (!v) return "vacio";
  if ("reason" in r && r.reason === "control") return "letra_control";
  if (/[^0-9A-Z]/.test(v)) return "caracteres";
  const cifras = (s: string) => s.replace(/\D/g, "").length;
  if (/^\d+$/.test(v)) return v.length === 8 ? "sin_letra" : v.length === 9 ? "cifra_de_mas" : v.length < 8 ? "cifras_de_menos" : "cifras_de_mas";
  // DNI: 8 cifras + letra.
  if (/^\d+[A-Z]$/.test(v)) return cifras(v) < 8 ? "cifras_de_menos" : "cifras_de_mas";
  // NIE y NIF especiales: letra + 7 cifras + letra.
  if (/^[XYZKLM]\d+[A-Z]?$/.test(v)) {
    const n = cifras(v);
    if (!/[A-Z]$/.test(v.slice(1))) return n === 7 ? "sin_letra" : n < 7 ? "cifras_de_menos" : "cifra_de_mas";
    return n < 7 ? "cifras_de_menos" : "cifras_de_mas";
  }
  // CIF: letra + 7 cifras + control (cifra o letra) = 9 caracteres.
  if (/^[ABCDEFGHJNPQRSUVW][0-9A-J]*$/.test(v)) return v.length < 9 ? "cifras_de_menos" : "cifras_de_mas";
  return "forma";
}

/** La forma de un NIF, para decirla con un ejemplo (el asistente la dice en el idioma de quien pregunta). */
export const FORMATO_NIF = "DNI: 8 digits and a letter (e.g. 12345678Z) · NIE: X, Y or Z + 7 digits + a letter (X1234567L) · company CIF: a letter + 8 characters (B12345674)";

// ── ROMA y ROPO ─────────────────────────────────────────────────────────────
//
// Ni el ROMA (Registro Oficial de Maquinaria Agrícola) ni el ROPO (Registro
// Oficial de Productores y Operadores) tienen un formato único publicado: cada
// comunidad los escribió a su manera y conviven «AL-0412-2019», «04/1234/AS» o
// «0400123456S». No tienen dígito de control. Lo que se comprueba es lo que
// SÍ caza erratas y basura: caracteres posibles, longitud y al menos tres cifras
// (un «no sé», «Es» o «pendiente» no pasa).

export type CodeCheck = { ok: true; value: string } | { ok: false; value: string; reason: "empty" | "format" };

function checkRegistryCode(raw: string | null | undefined, max: number): CodeCheck {
  const value = String(raw ?? "").trim().toUpperCase().replace(/\s+/g, " ");
  if (!value) return { ok: false, value, reason: "empty" };
  if (value.length < 4 || value.length > max) return { ok: false, value, reason: "format" };
  if (!/^[A-Z0-9][A-Z0-9 ./-]*$/.test(value)) return { ok: false, value, reason: "format" };
  if ((value.match(/\d/g) ?? []).length < 3) return { ok: false, value, reason: "format" };
  return { ok: true, value };
}

/** Nº de inscripción en el ROMA (o de censo, si el equipo no va al ROMA). */
export const checkRoma = (raw: string | null | undefined): CodeCheck => checkRegistryCode(raw, 30);
/** Nº de inscripción en el ROPO (carné de aplicador o asesor). */
export const checkRopo = (raw: string | null | undefined): CodeCheck => checkRegistryCode(raw, 30);

// ── Código postal ───────────────────────────────────────────────────────────

/** Cinco cifras y una provincia que existe (01 Álava … 52 Melilla). */
export function isValidCodigoPostal(raw: string | null | undefined): boolean {
  const v = String(raw ?? "").trim();
  if (!/^\d{5}$/.test(v)) return false;
  const prov = Number(v.slice(0, 2));
  return prov >= 1 && prov <= 52;
}

// ── SIGPAC ──────────────────────────────────────────────────────────────────

/** Los siete códigos de un recinto, SIN ceros delante («4», «102», «11»…). */
export interface SigpacCodes {
  provincia: string;
  municipio: string;
  agregado: string;
  zona: string;
  poligono: string;
  parcela: string;
  recinto: string;
}

const SIGPAC_LIMITS: Record<keyof SigpacCodes, [number, number]> = {
  provincia: [1, 52],
  municipio: [1, 999],
  agregado: [0, 999],
  zona: [0, 999],
  poligono: [1, 999],
  parcela: [1, 99999],
  recinto: [1, 99999],
};

const SIGPAC_ORDER: (keyof SigpacCodes)[] = ["provincia", "municipio", "agregado", "zona", "poligono", "parcela", "recinto"];

const stripZeros = (s: string) => s.replace(/^0+(?=\d)/, "");

export type SigpacCheck =
  | { ok: true; codes: SigpacCodes }
  | { ok: false; field: keyof SigpacCodes | null; reason: "format" | "range" };

/** Comprueba los siete códigos (vale con o sin ceros delante) y los devuelve limpios. */
export function checkSigpac(codes: Partial<Record<keyof SigpacCodes, string | number | null | undefined>>): SigpacCheck {
  const out = {} as SigpacCodes;
  for (const k of SIGPAC_ORDER) {
    const raw = String(codes[k] ?? (k === "agregado" || k === "zona" ? "0" : "")).trim();
    if (!/^\d{1,5}$/.test(raw)) return { ok: false, field: k, reason: "format" };
    const n = Number(raw);
    const [min, max] = SIGPAC_LIMITS[k];
    if (n < min || n > max) return { ok: false, field: k, reason: "range" };
    out[k] = stripZeros(raw);
  }
  return { ok: true, codes: out };
}

/**
 * «4 102 0 0 11 382 9», «04-102-0-0-011-00382-00009», «04:102:0:0:11:382:9» →
 * los siete códigos. Con seis números se entiende que falta el agregado Y la
 * zona no: no se adivina, se rechaza.
 */
export function parseSigpac(text: string | null | undefined): SigpacCheck {
  const parts = String(text ?? "").trim().split(/[\s\-:/.,]+/).filter(Boolean);
  if (parts.length !== 7) return { ok: false, field: null, reason: "format" };
  const codes = Object.fromEntries(SIGPAC_ORDER.map((k, i) => [k, parts[i]])) as unknown as SigpacCodes;
  return checkSigpac(codes);
}

/** «04-102-0-0-011-00382-00009»: como lo escribe el visor del SIGPAC (y el diseñador). */
export function formatSigpac(codes: SigpacCodes): string {
  return [
    codes.provincia.padStart(2, "0"),
    codes.municipio.padStart(3, "0"),
    codes.agregado,
    codes.zona,
    codes.poligono.padStart(3, "0"),
    codes.parcela.padStart(5, "0"),
    codes.recinto.padStart(5, "0"),
  ].join("-");
}
